// Route paths
export const ROUTES = {
  HOME: '/',
  GAMES: '/games',
  PICK: '/games/:gameId/pick',
  MY_PICKS: '/my-picks',
  ARENA: '/arena',
  BOUT: '/arena/bouts/:boutId',
  PROFILE: '/profile',
  PROFILE_SETTINGS: '/profile/settings',
  PROFILE_FRIENDS: '/profile/friends',
  PROFILE_STATS: '/profile/stats',
  PROFILE_BOUT_HISTORY: '/profile/bout-history',
  USER_PROFILE: '/users/:username',
  LOGIN: '/login',
  REGISTER: '/register',
};

// Sport types
export const SPORTS = {
  NFL: 'nfl',
  NBA: 'nba',
  MLB: 'mlb',
  NHL: 'nhl',
  SOCCER: 'soccer',
};

// League configuration
export const LEAGUES = {
  nfl: { name: 'NFL', slug: 'nfl', sport: SPORTS.NFL, emoji: '🏈' },
  nba: { name: 'NBA', slug: 'nba', sport: SPORTS.NBA, emoji: '🏀' },
  mlb: { name: 'MLB', slug: 'mlb', sport: SPORTS.MLB, emoji: '⚾' },
  nhl: { name: 'NHL', slug: 'nhl', sport: SPORTS.NHL, emoji: '🏒' },
  ncaab: { name: 'NCAAB', slug: 'ncaab', sport: 'ncaab', emoji: '🏀' },
  ncaaf: { name: 'NCAAF', slug: 'ncaaf', sport: 'ncaaf', emoji: '🏈' },
  epl: { name: 'Premier League', slug: 'epl', sport: SPORTS.SOCCER, emoji: '⚽' },
  mls: { name: 'MLS', slug: 'mls', sport: SPORTS.SOCCER, emoji: '⚽' },
  laliga: { name: 'La Liga', slug: 'laliga', sport: SPORTS.SOCCER, emoji: '⚽' },
  seriea: { name: 'Serie A', slug: 'seriea', sport: SPORTS.SOCCER, emoji: '⚽' },
  bundesliga: { name: 'Bundesliga', slug: 'bundesliga', sport: SPORTS.SOCCER, emoji: '⚽' },
  ucl: { name: 'Champions League', slug: 'ucl', sport: SPORTS.SOCCER, emoji: '⚽' },
  wnba: { name: 'WNBA', slug: 'wnba', sport: 'wnba', emoji: '🏀' },
};

// Game statuses
export const GAME_STATUS = {
  SCHEDULED: 'scheduled',
  IN_PROGRESS: 'in_progress',
  FINAL: 'final',
  POSTPONED: 'postponed',
  CANCELLED: 'cancelled',
};

// Pick statuses
export const PICK_STATUS = {
  PENDING: 'pending',
  LOCKED: 'locked',
  WON: 'won',
  LOST: 'lost',
  PUSH: 'push',
  CANCELLED: 'cancelled',
};

// Pick sides
export const PICK_SIDE = {
  HOME: 'home',
  AWAY: 'away',
};

// Badge types
export const BADGE_TYPE = {
  DEFAULT: 'default',
  ACHIEVEMENT: 'achievement',
};

// Badge definitions — default avatars + achievement badges
export const BADGES = {
  // ── Default Avatars (free, selectable by anyone) ──
  newbie:       { id: 'newbie',       emoji: '🐣', name: 'Newbie',       description: 'Just getting started',            type: BADGE_TYPE.DEFAULT },
  fire:         { id: 'fire',         emoji: '🔥', name: 'Fire',         description: 'Bringing the heat',               type: BADGE_TYPE.DEFAULT },
  lightning:    { id: 'lightning',    emoji: '⚡', name: 'Lightning',    description: 'Quick picker',                    type: BADGE_TYPE.DEFAULT },
  star:         { id: 'star',         emoji: '⭐', name: 'Star',         description: 'Reach for the stars',             type: BADGE_TYPE.DEFAULT },
  rocket:       { id: 'rocket',       emoji: '🚀', name: 'Rocket',       description: 'To the moon',                    type: BADGE_TYPE.DEFAULT },
  ghost:        { id: 'ghost',        emoji: '👻', name: 'Ghost',        description: 'Boo!',                           type: BADGE_TYPE.DEFAULT },
  alien:        { id: 'alien',        emoji: '👽', name: 'Alien',        description: 'Out of this world',              type: BADGE_TYPE.DEFAULT },
  robot:        { id: 'robot',        emoji: '🤖', name: 'Robot',        description: 'Calculated picks',               type: BADGE_TYPE.DEFAULT },

  // ── Achievement Badges (earned, auto-awarded by Cloud Functions) ──
  first_pick:   { id: 'first_pick',   emoji: '🎯', name: 'First Pick',   description: 'Made your first pick',           type: BADGE_TYPE.ACHIEVEMENT },
  hot_hand:     { id: 'hot_hand',     emoji: '🖐️', name: 'Hot Hand',     description: '3-win streak',                   type: BADGE_TYPE.ACHIEVEMENT },
  on_fire:      { id: 'on_fire',      emoji: '☄️', name: 'On Fire',      description: '5-win streak',                   type: BADGE_TYPE.ACHIEVEMENT },
  unstoppable:  { id: 'unstoppable',  emoji: '💎', name: 'Unstoppable',  description: '10-win streak',                  type: BADGE_TYPE.ACHIEVEMENT },
  legend:       { id: 'legend',       emoji: '👑', name: 'Legend',        description: '20-win streak',                  type: BADGE_TYPE.ACHIEVEMENT },
  veteran:      { id: 'veteran',      emoji: '🎖️', name: 'Veteran',      description: '50 total picks',                 type: BADGE_TYPE.ACHIEVEMENT },
  centurion:    { id: 'centurion',    emoji: '💯', name: 'Centurion',    description: '100 total picks',                type: BADGE_TYPE.ACHIEVEMENT },
  sharpshooter: { id: 'sharpshooter', emoji: '🏹', name: 'Sharpshooter', description: '25 correct picks',              type: BADGE_TYPE.ACHIEVEMENT },
  sniper:       { id: 'sniper',       emoji: '🎯', name: 'Sniper',       description: '75 correct picks',              type: BADGE_TYPE.ACHIEVEMENT },
  comeback:     { id: 'comeback',     emoji: '🔄', name: 'Comeback Kid',  description: 'Win after a loss',              type: BADGE_TYPE.ACHIEVEMENT },
};

// All default badge IDs (always selectable by anyone)
export const DEFAULT_BADGE_IDS = Object.values(BADGES)
  .filter(b => b.type === BADGE_TYPE.DEFAULT)
  .map(b => b.id);

// All achievement badge IDs
export const ACHIEVEMENT_BADGE_IDS = Object.values(BADGES)
  .filter(b => b.type === BADGE_TYPE.ACHIEVEMENT)
  .map(b => b.id);

// Bout configuration
export const BOUT_TARGETS = [5, 10, 20];

export const BOUT_STATUS = {
  OPEN: 'open',
  ACTIVE: 'active',
  COMPLETED: 'completed',
};

export const BOUT_VISIBILITY = {
  PUBLIC: 'public',
  PRIVATE: 'private',
};
